import { USER_INFO_BANNER_FLAG } from '@/constants/experimentConstants';
import { USER_INFO_BANNER_OFFSET_VAR } from '@/constants/styleConstants';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';
import { RefObject, useEffect, useState } from 'react';
import {
  checkIfDismissedInSession,
  getActiveBanner,
  saveDismissedInSession,
  SEVERITY_BY_MESSAGE,
} from './UserInfoBanner.helpers';
import { ActiveUserInfoBanner, UserInfoBannerMessage } from './UserInfoBanner.types';

export const useUserInfoBanner = (): { banner: ActiveUserInfoBanner | null; dismiss: () => void } => {
  const { flagValue } = useConfidenceFlag(USER_INFO_BANNER_FLAG.name, USER_INFO_BANNER_FLAG.defaultValue);
  const [dismissedMessage, setDismissedMessage] = useState<UserInfoBannerMessage | null>(null);

  const activeBanner = getActiveBanner(flagValue);

  const dismiss = () => {
    if (!activeBanner) return;
    saveDismissedInSession(activeBanner.message);
    setDismissedMessage(activeBanner.message);
  };

  const isDismissed =
    activeBanner !== null &&
    (dismissedMessage === activeBanner.message || checkIfDismissedInSession(activeBanner.message));

  if (!activeBanner || isDismissed) {
    return { banner: null, dismiss };
  }

  return {
    banner: {
      message: activeBanner.message,
      severity: SEVERITY_BY_MESSAGE[activeBanner.message],
      dismissable: activeBanner.dismissable,
    },
    dismiss,
  };
};

// The banner scrolls away with the page, so the offset is its on-screen part
// (bottom edge clamped at 0), not its full height.
export const useUserInfoBannerOffset = (ref: RefObject<HTMLElement>, isShown: boolean) => {
  useEffect(() => {
    const element = ref.current;
    if (!isShown || !element) return;

    const root = document.documentElement;
    const update = () => {
      const visibleHeight = Math.max(0, element.getBoundingClientRect().bottom);
      root.style.setProperty(USER_INFO_BANNER_OFFSET_VAR, `${visibleHeight}px`);
    };

    update();
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    resizeObserver?.observe(element);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      root.style.removeProperty(USER_INFO_BANNER_OFFSET_VAR);
    };
  }, [ref, isShown]);
};
